import React from 'react';
import { Box, Divider, makeStyles, Typography } from '@material-ui/core';
import SubText from './SubText';
import { KeyStudentEngagementAssessmentAcceleratorModuleResult } from 'types/schema-generated-types';
import { pick } from 'lodash';
import formatUiDate from 'utilities/format-ui-date';
import PopoverWrap from 'components/@common/Popover';
import { useTranslation } from 'react-i18next';
import { calcMinutes } from 'components/ReportsModule/components/KeyboardingReport/keyboarding-report-util';

export const AssessmentDetails: React.FC<Partial<
  KeyStudentEngagementAssessmentAcceleratorModuleResult
> & { isAllModule?: boolean }> = ({
  totalSecondsSpent,
  totalFirstAttemptCount,
  averageAttemptCount,
  dateCompleted,
  isAllModule
}) => {
  const { t } = useTranslation('ReportsHelpCards');
  return (
    <Box display="flex" width="100%" justifyContent="space-between" marginBottom={4}>
      <Box>
        <PopoverWrap description={t('completedStatus')}>
          <Typography>Status</Typography>
        </PopoverWrap>
        <Typography variant="h1">
          {t('completed')} <SubText>on {formatUiDate(dateCompleted, 'MM/dd/yyyy')}</SubText>
        </Typography>
      </Box>
      <Box>
        <PopoverWrap
          description={
            isAllModule
              ? t('allModulesTimeToCompleteHelp', { ns: 'ReportsHelpCards' })
              : t('singleModuleTimeToCompleteHelp', { ns: 'ReportsHelpCards' })
          }
        >
          <Typography>{t('timeToComplete')}</Typography>
        </PopoverWrap>
        <Typography variant="h1">{calcMinutes(totalSecondsSpent!)}</Typography>
      </Box>
      <Box>
        <PopoverWrap description={t('numberOfQuestionsAnsweredHelp')}>
          <Typography>{t('numberOfQuestionsAnswered')}</Typography>
        </PopoverWrap>
        <Typography variant="h1">
          {totalFirstAttemptCount}
          <SubText> questions</SubText>
        </Typography>
      </Box>
      <Box>
        <PopoverWrap description={t('averageTriesPerQuestionHelp')}>
          <Typography>{t('averageTriesPerQuestion')}</Typography>
        </PopoverWrap>
        <Typography variant="h1">
          {averageAttemptCount} <SubText>tries</SubText>
        </Typography>
      </Box>
    </Box>
  );
};

type HeaderProps = {
  showDivider: boolean;
  firstAttempt: string;
  secondAttempt: string;
  moduleData: KeyStudentEngagementAssessmentAcceleratorModuleResult;
  title: string;
  showAttempt?: boolean;
  hasTwoAttempts?: boolean;
  isAllModule?: boolean;
};

const SingleModuleAttemptHeader: React.FC<HeaderProps> = ({
  title,
  showDivider = true,
  firstAttempt = '',
  secondAttempt = '',
  moduleData,
  showAttempt = true,
  hasTwoAttempts,
  isAllModule
}) => {
  const classes = useStyles();

  return (
    <>
      <Typography variant="h4" style={{ marginBottom: 32 }}>
        {title}
      </Typography>
      <AssessmentDetails
        {...(pick(moduleData, [
          'totalSecondsSpent',
          'totalFirstAttemptCount',
          'averageAttemptCount',
          'dateCompleted'
        ]) as any)}
        isAllModule={isAllModule}
      />
      {showDivider && <Divider style={{ marginBottom: 24 }} />}
      {showAttempt && (
        <Box className={classes.container}>
          <Typography
            className={classes.headers}
            style={{ marginRight: 10, flex: !hasTwoAttempts ? 0.8 : 0.4 }}
          >
            {secondAttempt}
          </Typography>
          {hasTwoAttempts && (
            <Typography className={classes.headers} style={{ flex: 0.45 }}>
              {firstAttempt}
            </Typography>
          )}
        </Box>
      )}
    </>
  );
};

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flex: 0.85,
    padding: 1,
    justifyContent: 'flex-end'
  },
  headers: {
    fontSize: 12,
    backgroundColor: '#EAEAEA',
    textAlign: 'center',
    lineHeight: 'unset',
    fontWeight: 600
  }
}));

export default SingleModuleAttemptHeader;
